import { z } from "zod";
//#region src/deployment-config.ts
/**
* The response schemas of the CP deployment-config routes (2026-10-12-shared-libraries, R-008,
* R-017).
*
* The deployment-config registry is CP config, so it leaves the CP-DP contract and moves into
* release-engine (R-008). The wire shapes of its routes stay in a contract package, because
* `tools/zion-ops` parses them: they move here from `deployment-config.schemas.ts` of
* `@zion/sdk-cp-dp-contracts`, under the same names. Each entry gains `enumValues`, `format` and
* `description`, so `tools/zion-ops` validates a value with the definition the CP serves and needs
* no compiled-in registry (R-017).
*
* Producer: release-engine `GET /v1/deployment-config` and `GET /v1/deployment-config/:key/history`
* (WS-4). Reader: `tools/zion-ops` (WS-16). The copies in `@zion/sdk-cp-dp-contracts` stay until
* the contract release of stage 5 removes them (WS-16).
*
* Pure and browser-safe.
*/
/** The value types a deployment-config definition may declare. */
const DEPLOYMENT_CONFIG_VALUE_TYPES = Object.freeze([
	"boolean",
	"string",
	"number",
	"enum"
]);
/** The delivery classes of a deployment-config definition. */
const DEPLOYMENT_CONFIG_CLASSES = Object.freeze(["topology", "runtime"]);
/** The string formats a deployment-config definition may declare. */
const DEPLOYMENT_CONFIG_FORMATS = Object.freeze(["lowercased-uuid"]);
/** The `default` primitive union (boolean, string or number). */
const configPrimitiveSchema = z.union([
	z.boolean(),
	z.string(),
	z.number()
]);
/**
* One entry of the `GET /v1/deployment-config` response. Strict. `stored` is `null` when unset;
* `effective` is `stored ?? default` on the CP (`z.unknown()` admits `null`). `description` is the
* definition's text. `tools/zion-ops` checks a value with the definition the CP serves (R-017), so
* the entry must carry a whole definition. The schema refuses (DEF-3):
*
* 1. an `enum` entry with no `enumValues` (path `enumValues`): a reader could not check a value;
* 2. `enumValues` on an entry that is not `enum` (path `enumValues`);
* 3. `format` on an entry that is not `string` (path `format`).
*
* `enumValues` holds 1 or more allowed values. `format` names a string format.
*/
const DeploymentConfigEntryDtoSchema = z.object({
	key: z.string(),
	valueType: z.enum(DEPLOYMENT_CONFIG_VALUE_TYPES),
	configClass: z.enum(DEPLOYMENT_CONFIG_CLASSES),
	scope: z.literal("deployment"),
	stored: z.unknown(),
	effective: z.unknown(),
	default: configPrimitiveSchema,
	enumValues: z.array(z.string()).min(1).optional(),
	format: z.enum(DEPLOYMENT_CONFIG_FORMATS).optional(),
	description: z.string()
}).strict().superRefine((entry, ctx) => {
	if (entry.valueType === "enum" && entry.enumValues === void 0) ctx.addIssue({
		code: "custom",
		path: ["enumValues"],
		message: `enum key '${entry.key}' declares no enumValues`
	});
	if (entry.valueType !== "enum" && entry.enumValues !== void 0) ctx.addIssue({
		code: "custom",
		path: ["enumValues"],
		message: `${entry.valueType} key '${entry.key}' declares enumValues`
	});
	if (entry.valueType !== "string" && entry.format !== void 0) ctx.addIssue({
		code: "custom",
		path: ["format"],
		message: `${entry.valueType} key '${entry.key}' declares a format`
	});
});
/** The `GET /v1/deployment-config` response envelope. Strict. */
const DeploymentConfigListDtoSchema = z.object({ entries: z.array(DeploymentConfigEntryDtoSchema) }).strict();
/**
* One audit row of the `GET /v1/deployment-config/:key/history` response. Strict. `oldValue` is
* `null` on the first set; `setAt` is an ISO timestamp.
*/
const DeploymentConfigHistoryEntryDtoSchema = z.object({
	id: z.string(),
	key: z.string(),
	scope: z.literal("deployment"),
	oldValue: z.unknown(),
	newValue: z.unknown(),
	setBy: z.string(),
	setAt: z.string()
}).strict();
/** The `GET /v1/deployment-config/:key/history` response envelope. Strict. */
const DeploymentConfigHistoryDtoSchema = z.object({
	key: z.string(),
	entries: z.array(DeploymentConfigHistoryEntryDtoSchema)
}).strict();
//#endregion
export { DEPLOYMENT_CONFIG_CLASSES, DEPLOYMENT_CONFIG_FORMATS, DEPLOYMENT_CONFIG_VALUE_TYPES, DeploymentConfigEntryDtoSchema, DeploymentConfigHistoryDtoSchema, DeploymentConfigHistoryEntryDtoSchema, DeploymentConfigListDtoSchema };

//# sourceMappingURL=deployment-config.js.map